import { Show, createSignal, type Component } from "solid-js";
import { PanelLayout, type SubTab, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { PanelBadge, type Intent } from "@/shared/panel-primitives";
import { registerPanel } from "@/panel-registry";
import { highlightToHtml } from "./lib/highlight";
import "./docker.css";

interface DockerContainer {
  ID?: string;
  Names?: string;
  Image?: string;
  State?: string;
  Status?: string;
  Ports?: string;
  Networks?: string;
}

interface DockerImage {
  ID?: string;
  Repository?: string;
  Tag?: string;
  Size?: string;
  CreatedSince?: string;
}

interface DockerNetwork {
  ID?: string;
  Name?: string;
  Driver?: string;
  Scope?: string;
}

interface ListResult<T> {
  rows?: T[];
  ok?: boolean;
  error?: string;
}

interface LogsResult {
  output?: string;
  ok?: boolean;
  error?: string;
}

function shortId(id: string | undefined): string {
  const s = String(id || "");
  return s.length > 12 ? s.slice(0, 12) : s;
}

function isRunning(state: string | undefined): boolean {
  return String(state || "").toLowerCase() === "running";
}

const DockerPanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [showAll, setShowAll] = createSignal(false);
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);
  const [logsOpen, setLogsOpen] = createSignal<{ name: string; output: string } | null>(null);

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const containersQuery = createToolQuery<ListResult<DockerContainer>>(() => ({
    tool: "docker_containers_list",
    params: { all: showAll(), limit: 200 },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const imagesQuery = createToolQuery<ListResult<DockerImage>>(() => ({
    tool: "docker_images_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const networksQuery = createToolQuery<ListResult<DockerNetwork>>(() => ({
    tool: "docker_networks_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  function dockerUnavailable(): boolean {
    const c = containersQuery.data;
    if (c && c.ok === false && c.error === "docker_unavailable") return true;
    const i = imagesQuery.data;
    if (i && i.ok === false && i.error === "docker_unavailable") return true;
    return false;
  }

  async function runContainerAction(
    id: string,
    name: string,
    action: "start" | "stop" | "restart" | "remove",
  ): Promise<void> {
    if (!id) return;
    const isDanger = action === "stop" || action === "remove";
    const labels: Record<string, string> = {
      start: "Demarrer",
      stop: "Arreter",
      restart: "Redemarrer",
      remove: "Supprimer",
    };
    const actionLabel = labels[action] || action;
    const ok = await confirm.confirm({
      title: `${actionLabel} - ${name}`,
      body: `Confirmer l'action Docker "${actionLabel}" sur le conteneur ${name} (${shortId(id)}) ?`,
      level: isDanger ? "danger" : "warn",
      confirmLabel: actionLabel,
      entityName: isDanger ? name : "",
    });
    if (!ok) return;
    setActionStatus({ text: `Execution ${actionLabel}...`, level: "info" });
    try {
      await tools.call(
        "docker_container_action",
        { containerId: id, action },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: `Action ${actionLabel} sur ${shortId(id)} reussie.`, level: "info" });
      await containersQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function showLogs(id: string, name: string): Promise<void> {
    if (!id) return;
    setLogsOpen({ name, output: "Chargement des logs..." });
    try {
      const r = await tools.call<LogsResult>("docker_container_logs", {
        containerId: id,
        tail: 500,
      });
      setLogsOpen({ name, output: String(r?.output || "(aucune sortie)") });
    } catch (err) {
      setLogsOpen({
        name,
        output: `Erreur logs: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  }

  async function removeImage(id: string): Promise<void> {
    if (!id) return;
    const short = shortId(id);
    const ok = await confirm.confirm({
      title: `Supprimer image ${short}`,
      body: `Confirmer la suppression de l'image Docker ${short} ? Cette operation est irreversible.`,
      level: "danger",
      confirmLabel: "Supprimer",
      entityName: short,
    });
    if (!ok) return;
    setActionStatus({ text: "Suppression image...", level: "info" });
    try {
      await tools.call(
        "docker_image_action",
        { imageId: id, action: "remove" },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: `Image ${short} supprimee.`, level: "info" });
      await imagesQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  const containerColumns: Column<DockerContainer>[] = [
    { accessorKey: "Names", header: "Names" },
    { accessorKey: "Image", header: "Image" },
    {
      accessorKey: "State",
      header: "State",
      cell: (info) => {
        const v = String(info.getValue() ?? "");
        const lvl: Intent = isRunning(v) ? "ok" : "warn";
        return <PanelBadge intent={lvl}>{v}</PanelBadge>;
      },
    },
    { accessorKey: "Status", header: "Status" },
    { accessorKey: "Ports", header: "Ports" },
    { accessorKey: "Networks", header: "Networks" },
    {
      accessorKey: "ID",
      header: "ID",
      cell: (info) => shortId(String(info.getValue() ?? "")),
    },
  ];

  const imageColumns: Column<DockerImage>[] = [
    { accessorKey: "Repository", header: "Repository" },
    { accessorKey: "Tag", header: "Tag" },
    { accessorKey: "Size", header: "Size" },
    { accessorKey: "CreatedSince", header: "CreatedSince" },
    {
      accessorKey: "ID",
      header: "ID",
      cell: (info) => shortId(String(info.getValue() ?? "")),
    },
  ];

  const networkColumns: Column<DockerNetwork>[] = [
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "Driver", header: "Driver" },
    { accessorKey: "Scope", header: "Scope" },
    {
      accessorKey: "ID",
      header: "ID",
      cell: (info) => shortId(String(info.getValue() ?? "")),
    },
  ];

  const toolbar = (
    <>
      <label class="panel-filter-checkbox">
        <input type="checkbox" checked={showAll()} onChange={(e) => setShowAll(e.currentTarget.checked)} />
        <span> Afficher arretes</span>
      </label>
      <button
        type="button"
        class="btn btn--xs btn--info"
        onClick={() => {
          containersQuery.refetch();
          imagesQuery.refetch();
          networksQuery.refetch();
        }}
      >
        Rechercher
      </button>
    </>
  );

  const subtabs: SubTab[] = [
    {
      id: "containers",
      label: "Containers",
      render: () => (
        <>
          <DataTable<DockerContainer>
            rows={containersQuery.data?.rows ?? []}
            columns={containerColumns}
            emptyMessage="Aucun conteneur."
            rowActions={(r) => {
              const id = String(r.ID || "");
              const name = String(r.Names || id);
              const running = isRunning(r.State);
              return (
                <>
                  <button
                    type="button"
                    class="btn btn--xs btn--ghost"
                    disabled={running}
                    onClick={() => runContainerAction(id, name, "start")}
                  >
                    Start
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--danger"
                    disabled={!running}
                    onClick={() => runContainerAction(id, name, "stop")}
                  >
                    Stop
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--warn"
                    onClick={() => runContainerAction(id, name, "restart")}
                  >
                    Restart
                  </button>
                  <button type="button" class="btn btn--xs btn--ghost" onClick={() => showLogs(id, name)}>
                    Logs
                  </button>
                  <button
                    type="button"
                    class="btn btn--xs btn--danger"
                    onClick={() => runContainerAction(id, name, "remove")}
                  >
                    Remove
                  </button>
                </>
              );
            }}
          />
          <Show when={logsOpen()}>
            {(l) => (
              <div
                class="panel-modal-overlay"
                style={{
                  position: "fixed",
                  inset: "0",
                  background: "rgba(0,0,0,0.45)",
                  display: "flex",
                  "align-items": "center",
                  "justify-content": "center",
                  "z-index": "9999",
                }}
                onClick={(e) => {
                  if (e.target === e.currentTarget) setLogsOpen(null);
                }}
              >
                <div
                  class="panel-modal"
                  style={{
                    background: "#1f2128",
                    color: "#e7e7e7",
                    "max-width": "80vw",
                    "max-height": "80vh",
                    width: "720px",
                    "border-radius": "8px",
                    display: "flex",
                    "flex-direction": "column",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      padding: "0.75em 1em",
                      "border-bottom": "1px solid #333",
                      display: "flex",
                      "justify-content": "space-between",
                      "align-items": "center",
                    }}
                  >
                    <strong>Logs - {l().name}</strong>
                    <button type="button" class="btn btn--xs btn--ghost" onClick={() => setLogsOpen(null)}>
                      Fermer
                    </button>
                  </div>
                  {/* innerHTML reçoit uniquement du HTML déjà échappé par
                      highlightToHtml — aucun contenu de log brut n'est injecté. */}
                  {/* eslint-disable-next-line solid/no-innerhtml -- highlightToHtml échappe tout le contenu */}
                  <pre class="docker-log-view" innerHTML={highlightToHtml(l().output, "log")} />
                </div>
              </div>
            )}
          </Show>
        </>
      ),
    },
    {
      id: "images",
      label: "Images",
      render: () => (
        <DataTable<DockerImage>
          rows={imagesQuery.data?.rows ?? []}
          columns={imageColumns}
          emptyMessage="Aucune image."
          rowActions={(r) => (
            <button type="button" class="btn btn--xs btn--danger" onClick={() => removeImage(String(r.ID || ""))}>
              Remove
            </button>
          )}
        />
      ),
    },
    {
      id: "networks",
      label: "Networks",
      render: () => (
        <DataTable<DockerNetwork>
          rows={networksQuery.data?.rows ?? []}
          columns={networkColumns}
          emptyMessage="Aucun reseau."
        />
      ),
    },
  ];

  const status = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (dockerUnavailable()) return { text: "Docker indisponible sur ce poste.", level: "danger" };
    if (containersQuery.isFetching) return { text: "Chargement...", level: "info" };
    if (containersQuery.error)
      return {
        text: `Erreur: ${containersQuery.error instanceof Error ? containersQuery.error.message : String(containersQuery.error)}`,
        level: "danger",
      };
    return undefined;
  };

  return <PanelLayout title="Docker" subtabs={subtabs} toolbar={toolbar} status={status()} />;
};

registerPanel("docker", DockerPanel);
export default DockerPanel;
